import { zodResolver } from "@hookform/resolvers/zod"
import { ApiResponse, useCustomForm } from "./useCustomForm"
import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useCrudContext } from "@/context/crudContext"
import { toast } from "sonner"
import { ZodSchema } from "zod"

export const useAddModel = <T,>(
  url: string,
  schema: ZodSchema<T>,
  revalidateQuery?: string,
  onClose?: () => void,
  transformData?: (data: T) => T
) => {
  const queryClient = useQueryClient()
  const { openDialog, setOpenDialog, selectedRow, setSelectedRow } =
    useCrudContext()
  const [newRecord, setNewRecord] = useState<T | null>(null)

  const defaultOnClose = () => setOpenDialog(false)

  const {
    errors,
    control,
    isSubmitting,
    Controller,
    api,
    register,
    handleSubmit,
    reset,
    setValue,
  } = useCustomForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: selectedRow ? selectedRow.name : "",
      brand_name: selectedRow ? selectedRow.brand_name : "",
      location: selectedRow ? selectedRow.location : "",
      phone: selectedRow ? selectedRow.phone : "",
    },
  })

  // Helper function to set form values
  const setFormValues = (data?: T) => {
    if (data) {
      Object.keys(data).forEach((key) => {
        setValue(key as keyof T & string, data[key as keyof T] as any) // Narrow key to string type
      })
    } else {
      reset()
    }
  }

  const onSubmit = async (data: any) => {
    const method = selectedRow ? "PUT" : "POST"
    const apiUrl = selectedRow ? `${url}/${selectedRow.id}` : url

    const processedData = transformData ? transformData(data) : data

    const response = await api<ApiResponse<T>>(apiUrl, {
      method,
      body: JSON.stringify(processedData),
    })

    if (response) {
      setNewRecord(response.data as T)
      queryClient.invalidateQueries({
        queryKey: revalidateQuery ? [revalidateQuery] : [url],
      })
      toast.success(`Record successfully ${selectedRow ? "updated" : "added"}`)
      onClose ? onClose() : defaultOnClose()
      reset()
    }
  }

  const handleReset = () => {
    setFormValues(selectedRow as T)
  }

  return {
    errors,
    isSubmitting,
    openDialog,
    selectedRow,
    queryClient,
    control,
    newRecord,
    setNewRecord,
    Controller,
    register,
    handleSubmit,
    onSubmit,
    handleReset,
    defaultOnClose,
    setOpenDialog,
    setSelectedRow,
    setValue,
    api,
    reset,
  }
}
